import axios from "axios";
import { Logger } from "../logging/logger";
import { SiteflowResponse } from "../types/ContentfulTypes";
import { stringify } from "qs";
import { fireSiteflowExperiments } from "../utils/fireSiteflowExperimentImpression";

const BASE_URL = "https://siteflow.merch.vpsvc.com/";

let multiNextStepCache: Record<string, { flows: SiteflowResponse[] }> = {};
let actionCache: Record<string, SiteflowResponse> = {};

type ExperimentDefinition = {
  experiment: string;
  variation: string;
};

type SiteflowServiceResponse = {
  action: "Navigate" | "None";
  url: string;
  urls: string[];
  flowAction: "nextStep";
  type: string;
  predictedFlow: null;
  evaluationSteps: null;
  context: ExperimentDefinition[];
};

interface UrlTokens {
  documentUrl?: string;
  designTemplate?: string;
  previewInstructionsUrl?: string;
}

const decideCorrectUrl = (
  siteflowResponseUrls: string[] = [],
  siteflowResponseUrl: string,
  urlTokens: UrlTokens
): string => {
  const siteflowUrlsWithReplacedTokens = replaceTokens(siteflowResponseUrls, urlTokens);
  const bestUrlWithNoResponseTokens = siteflowUrlsWithReplacedTokens
    .reverse()
    .find((url: string) => !url.includes("{") && !url.includes("}"));

  return bestUrlWithNoResponseTokens || siteflowResponseUrl;
};

function replaceTokens(urls: string[] = [], tokens: Record<string, any>): string[] {
  if (!urls) return [];

  // if no replacements, return the url
  if (!tokens || Object.keys(tokens).length === 0 || !Object.values(tokens).some(value => !!value))
    return [...urls];

  // Build a regex with all the keys in replacements surrounded by curly braces as options
  const regexWithTokensInCurlyBraces = new RegExp(
    Object.keys(tokens)
      .filter(key => tokens[key] !== undefined)
      .map(key => `{${key}}`)
      .join("|"),
    "g"
  );

  const urlsWithReplacedTokens = urls.map((url: string) =>
    url.replace(regexWithTokensInCurlyBraces, (match: string) => {
      const key = removeCurlyBraces();
      return tokens[key];

      function removeCurlyBraces() {
        return match.slice(1, -1);
      }
    })
  );
  return urlsWithReplacedTokens;
}

const transformToSimplifiedResult = (
  siteflowServiceResponse: SiteflowServiceResponse,
  requestUrl: string,
  urlTokens: UrlTokens = {}
): SiteflowResponse | null => {
  if (!siteflowServiceResponse.urls && !siteflowServiceResponse.url) {
    return null;
  }

  return {
    url: decideCorrectUrl(siteflowServiceResponse.urls, siteflowServiceResponse.url, urlTokens),
    type: siteflowServiceResponse.type,
    requestUrl,
    context: siteflowServiceResponse.context,
  };
};

const getMultiNextStepCacheKey = (props: {
  tenant: string;
  culture: string;
  productKey: string;
  version: string | number;
  optionValues: Record<string, string>;
  quantity: string | number;
  optimizelyEndUserId: string;
  isMobile: boolean;
  urlTokens?: UrlTokens;
}) => {
  const {
    tenant,
    culture,
    productKey,
    version,
    optionValues,
    quantity,
    optimizelyEndUserId,
    isMobile,
    urlTokens,
  } = props;
  const stringifiedOptionValues = stringifyObjectForCache(optionValues);
  const stringifiedUrlTokens = stringifyObjectForCache(urlTokens as Record<string, string>);
  return `MultiNextStep;${tenant};${culture};${productKey};${version}:${stringifiedOptionValues};${quantity};${optimizelyEndUserId};isMobile:${isMobile};${stringifiedUrlTokens}`;
};

const getSiteflowMultiNextStepUrl = (props: {
  tenant: string;
  culture: string;
  productKey: string;
  version: string | number;
  optionValues: Record<string, string>;
  quantity: string | number;
  optimizelyEndUserId: string;
  isMobile: boolean;
}) => {
  const {
    tenant,
    culture,
    productKey,
    version,
    optionValues,
    quantity,
    optimizelyEndUserId,
    isMobile,
  } = props;
  return `${BASE_URL}v2/multinextstep/${tenant}/${culture}/ProductPage/${productKey}/${version}?${stringify(
    {
      optionValues,
      quantity,
      optimizelyEndUserId,
      isMobile,
      requestor: "ProductPageV2",
    }
  )}`;
};

const getMultiNextStep = async (props: {
  tenant: string;
  culture: string;
  productKey: string;
  version: string | number;
  optionValues: Record<string, string>;
  quantity: string | number;
  optimizelyEndUserId: string;
  isMobile: boolean;
  urlTokens?: UrlTokens;
  mpvId: string;
}): Promise<{ flows: SiteflowResponse[] }> => {
  const cacheKey = getMultiNextStepCacheKey(props);
  if (multiNextStepCache[cacheKey]) {
    for (const simplifiedFlow of multiNextStepCache[cacheKey]?.flows || []) {
      await fireSiteflowExperiments(simplifiedFlow, props.mpvId);
    }

    return multiNextStepCache[cacheKey];
  }
  const siteflowUrl = getSiteflowMultiNextStepUrl(props);
  try {
    const response = await axios.get(siteflowUrl);
    const simplifiedFlows: SiteflowResponse[] = (response.data?.flows || [])
      .map((flow: SiteflowServiceResponse) =>
        transformToSimplifiedResult(flow, siteflowUrl, props.urlTokens)
      )
      .filter((flow: SiteflowResponse | null) => flow !== null);

    for (const simplifiedFlow of simplifiedFlows) {
      await fireSiteflowExperiments(simplifiedFlow, props.mpvId);
    }

    const simplifiedResponse = { flows: simplifiedFlows };
    multiNextStepCache[cacheKey] = simplifiedResponse;
    return simplifiedResponse;
  } catch (error) {
    Logger.instance.error("Error calling Siteflow", {
      error,
      url: siteflowUrl,
    });
    return { flows: [] };
  }
};

const getActionCacheKey = (props: {
  tenant: string;
  culture: string;
  flowAction: string;
  productKey: string;
  version: string | number;
  optionValues: Record<string, string>;
  quantity: string | number | undefined;
  optimizelyEndUserId: string;
  isMobile: boolean;
  urlTokens?: UrlTokens;
}) => {
  const {
    tenant,
    culture,
    flowAction,
    productKey,
    version,
    optionValues,
    quantity,
    optimizelyEndUserId,
    isMobile,
    urlTokens,
  } = props;
  const stringifiedOptionValues = stringifyObjectForCache(optionValues);
  const stringifiedUrlTokens = stringifyObjectForCache(urlTokens as Record<string, string>);
  return `Action;${tenant};${culture};${flowAction};${productKey};${version}:${stringifiedOptionValues};${quantity};${optimizelyEndUserId};isMobile:${isMobile};${stringifiedUrlTokens}`;
};

const stringifyObjectForCache = (object: Record<string, string> = {}) =>
  Object.keys(object)
    .sort()
    .map(key => `${key}:${object[key]}`)
    .join(",");

const getSiteflowActionUrl = (props: {
  tenant: string;
  culture: string;
  flowAction: string;
  productKey: string;
  version: string | number;
  optionValues: Record<string, string>;
  quantity: string | number | undefined;
  optimizelyEndUserId: string;
  isMobile: boolean;
}) => {
  const {
    tenant,
    culture,
    flowAction,
    productKey,
    version,
    optionValues,
    quantity,
    optimizelyEndUserId,
    isMobile,
  } = props;
  return `${BASE_URL}v2/action/${tenant}/${culture}/${flowAction}/${productKey}/${version}?${stringify(
    {
      optionValues,
      quantity,
      optimizelyEndUserId,
      isMobile,
      requestor: "ProductPageV2",
    }
  )}`;
};

const getAction = async (props: {
  tenant: string;
  culture: string;
  flowAction: string;
  productKey: string;
  version: string | number;
  optionValues: Record<string, string>;
  quantity: string | number | undefined;
  optimizelyEndUserId: string;
  isMobile: boolean;
  urlTokens?: UrlTokens;
  mpvId: string;
}): Promise<SiteflowResponse | undefined> => {
  const cacheKey = getActionCacheKey(props);
  if (actionCache[cacheKey]) {
    await fireSiteflowExperiments(actionCache[cacheKey], props.mpvId);
    return actionCache[cacheKey];
  }
  const siteflowUrl = getSiteflowActionUrl(props);
  try {
    const response = await axios.get(siteflowUrl);
    const simplifiedResponse = transformToSimplifiedResult(
      response.data,
      siteflowUrl,
      props.urlTokens
    );

    if (simplifiedResponse == null) return undefined;

    await fireSiteflowExperiments(simplifiedResponse, props.mpvId);
    actionCache[cacheKey] = simplifiedResponse;
    return simplifiedResponse;
  } catch (error) {
    Logger.instance.error("Error calling Siteflow", {
      error,
      url: siteflowUrl,
    });
    return undefined;
  }
};

// For testing
const clearCache = () => {
  multiNextStepCache = {};
  actionCache = {};
};

export { getMultiNextStep, getAction, clearCache };
